import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';
import { User } from '../models/User';
import { fromUnixTime, isAfter } from 'date-fns';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    isAuthenticatedSubject = new BehaviorSubject<boolean>(this.isJwtAuthenticated());
    currentUserSubject = new BehaviorSubject<User>(null);

    constructor(private http: HttpClient, private router: Router) {

    }

    async login(username: string, password: string, refreshToken = false): Promise<boolean> {
        const data = {
            username,
            password,
            refreshToken
        };

        try {
            const response = await firstValueFrom(this.http.post<any>('api:///command/login', data));
            this.setAccessToken(response.accessToken);
            this.setRefreshToken(response.refreshToken);
            this.updateIsAuthenticatedSubject(true);

            return true;
        } catch (error) {
            this.updateIsAuthenticatedSubject(false);
            this.updateCurrentUser(null);
            throw error.status === 400
                ? error.error.validationErrors[0].errorMessage
                : 'Unknown error';
        }
    }

    logout(addReturnUrl = true) {
        if (this.hasAccessToken()) {
            localStorage.clear();
        }

        this.updateIsAuthenticatedSubject(false);
        this.updateCurrentUser(null);

        const routerOptions = addReturnUrl ? { queryParams: { returnUrl: this.router.url } } : {};
        return this.router.navigate(['/login'], routerOptions);
    }

    async getCurrentUser(): Promise<any> {
        try {
            const data = await firstValueFrom(this.http.get('api:///query/CurrentUser'));
            const user = data ? new User(data) : null;

            this.updateIsAuthenticatedSubject(!!data);
            this.updateCurrentUser(user);

            return user;
        } catch (error) {
            this.updateIsAuthenticatedSubject(false);
            this.updateCurrentUser(null);

            throw error;
        }
    }

    refreshAccessToken() {
        return this.http
            .post<any>('api:///command/RefreshAccessToken', {
                refreshToken: this.getRefreshToken()
            })
            .pipe(tap(response => {
                this.setAccessToken(response.accessToken);
                this.setRefreshToken(response.refreshToken);
            }));
    }

    getAccessTokenKey() {
        return environment.accessToken;
    }

    getRefreshTokenKey() {
        return environment.refreshToken;
    }

    getAccessToken() {
        return localStorage.getItem(this.getAccessTokenKey());
    }

    getRefreshToken() {
        return localStorage.getItem(this.getRefreshTokenKey());
    }

    hasAccessToken() {
        return !!this.getAccessToken();
    }

    setAccessToken(accessToken: string) {
        return localStorage.setItem(this.getAccessTokenKey(), accessToken);
    }

    setRefreshToken(refreshToken: string) {
        return localStorage.setItem(this.getRefreshTokenKey(), refreshToken);
    }

    private updateIsAuthenticatedSubject(value) {
        if (this.isAuthenticatedSubject.getValue() !== value) {
            this.isAuthenticatedSubject.next(value);
        }
    }

    private updateCurrentUser(user) {
        if (this.currentUserSubject.getValue() !== user) {
            this.currentUserSubject.next(user);
        }
    }

    private parseJwt(token): any {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));

        return JSON.parse(jsonPayload);
    }

    getJwtPayload() {
        const token = localStorage.getItem(environment.accessToken);
        if (!token) {
            return null;
        }

        return this.parseJwt(token);
    }

    private isJwtAuthenticated() {
        try {
            const payload = this.getJwtPayload();

            if (!payload) {
                return false;
            }

            const expireDate = fromUnixTime(payload.exp);

            return isAfter(expireDate, new Date());
        } catch (ex) {
            console.error(ex);
            return false;
        }
    }
}
